// @section: home-page
import { ArrowUpRight, Check, MessageSquare, Users, BarChart3, Send, Building2, Plane, Car, Scissors } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SectionHeading } from "@/components/Brand";
import {
  HERO_IMAGE,
  PILLARS,
  FEATURES,
  METRICS,
  CASES,
  FLOW,
  PRICING,
  FAQS,
  CONTACT_URL,
} from "@/lib/site";

function scrollToId(id: string) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

const PILLAR_ICONS = [MessageSquare, Users, Send];
const CASE_ICONS = [Building2, Plane, Car, Scissors];

export default function Index() {
  return (
    <div id="top" className="min-h-screen bg-background">
      <Header />

      {/* @section: hero */}
      <section className="border-b border-border bg-background">
        <div className="container mx-auto px-4 md:px-6 py-16 md:py-24">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <div className="inline-flex items-center gap-2 rounded-[4px] border border-border bg-secondary px-3 py-1.5">
                <span className="h-2 w-2 rounded-[1px] bg-accent" />
                <span className="text-xs font-semibold tracking-wide text-secondary-foreground">
                  LINE公式アカウント連携 CRM
                </span>
              </div>
              <h1 className="mt-6 text-4xl md:text-5xl lg:text-[3.4rem] font-bold leading-[1.15] tracking-tight text-primary">
                LINEでつながる、
                <br />
                顧客と企業の
                <span className="text-accent">新しい関係。</span>
              </h1>
              <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-muted-foreground">
                「接客・商談管理」「会員システム構築」「メッセージ配信」を通じて接客データを蓄積。蓄積したデータで会員基盤を構築し、接客品質を高めるマーケティングツールです。
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href={CONTACT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-1.5 rounded-[4px] bg-accent px-7 py-3.5 text-base font-semibold text-accent-foreground hover:opacity-90 transition-opacity"
                >
                  お問い合わせ
                  <ArrowUpRight size={18} />
                </a>
                <button
                  onClick={() => scrollToId("features")}
                  className="inline-flex items-center justify-center rounded-[4px] border border-border bg-background px-7 py-3.5 text-base font-semibold text-primary hover:border-accent hover:text-accent transition-colors"
                >
                  機能を見る
                </button>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-[8px] border border-border bg-card p-3 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                <img
                  src={HERO_IMAGE}
                  alt="LINE公式アカウントとCRMダッシュボードの連携イメージ"
                  className="w-full rounded-[5px]"
                  loading="eager"
                  width={2848}
                  height={1600}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* @section: about */}
      <section id="about" className="py-20 md:py-28">
        <div className="container mx-auto px-4 md:px-6">
          <SectionHeading
            align="center"
            eyebrow="What is linect"
            title={<>LINE×Webで会員基盤を、<br className="hidden md:block" />かんたんに構築する</>}
            description="3つの基盤機能で接客データを蓄積し、会員システムの構築と接客品質の向上を実現します。"
          />

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {PILLARS.map((p, i) => {
              const Icon = PILLAR_ICONS[i];
              return (
                <div
                  key={p.no}
                  className="group rounded-[8px] border border-border bg-card p-7 shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                >
                  <div className="flex items-center justify-between">
                    <span className="grid place-items-center h-11 w-11 rounded-[3px] bg-primary text-white">
                      <Icon size={20} strokeWidth={1.6} />
                    </span>
                    <span className="font-mono text-sm font-medium text-muted-foreground">
                      {p.no}
                    </span>
                  </div>
                  <h3 className="mt-5 text-lg font-bold text-primary">{p.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">
                    {p.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* @section: features */}
      <section id="features" className="bg-secondary py-20 md:py-28">
        <div className="container mx-auto px-4 md:px-6">
          <SectionHeading
            eyebrow="Features"
            title="接客を、組織の力に変える4つの機能"
            description="蓄積したデータを起点に、会員獲得から接客、フォローアップまでを一気通貫で支援します。"
          />

          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {FEATURES.map((f) => (
              <div
                key={f.no}
                className="flex flex-col rounded-[8px] border border-border bg-card p-7 md:p-8 shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
              >
                <div className="flex items-center gap-3">
                  <span className="font-mono text-2xl font-bold text-accent">{f.no}</span>
                  <span className="rounded-[3px] bg-secondary px-2.5 py-1 text-xs font-semibold text-secondary-foreground">
                    {f.tag}
                  </span>
                </div>
                <h3 className="mt-4 text-xl font-bold leading-snug text-primary">
                  {f.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {f.desc}
                </p>
                <div className="mt-5 overflow-hidden rounded-[5px] border border-border bg-background">
                  <img
                    src={f.image}
                    alt={`${f.title}のイメージ図`}
                    className="w-full"
                    loading="lazy"
                    width={2304}
                    height={1728}
                  />
                </div>
                <ul className="mt-5 space-y-2.5 border-t border-border pt-5">
                  {f.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-2.5">
                      <span className="mt-0.5 grid place-items-center h-5 w-5 shrink-0 rounded-[2px] bg-accent/12 text-accent">
                        <Check size={13} strokeWidth={2.4} />
                      </span>
                      <span className="text-sm text-secondary-foreground">{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* @section: effect */}
      <section id="effect" className="py-20 md:py-28">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <SectionHeading
                eyebrow="Impact"
                title={<>接客の安定化・品質向上が、<br className="hidden md:block" />売上の向上につながる</>}
                description="対応に応じたヒアリングシートで接客のばらつきを防止。売上につながった対応を分析し、接客品質の向上と効率化を継続的に進められます。"
              />
              <div className="mt-8 space-y-4">
                {[
                  "応対者による接客のばらつきを防ぐ",
                  "売上につながった対応を分析し横展開",
                  "顧客理解にもとづくパーソナル接客で満足度向上",
                ].map((t) => (
                  <div key={t} className="flex items-start gap-3">
                    <span className="mt-1 grid place-items-center h-6 w-6 shrink-0 rounded-[3px] bg-primary text-white">
                      <Check size={14} strokeWidth={2.4} />
                    </span>
                    <p className="text-base text-secondary-foreground">{t}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              {METRICS.map((m) => (
                <div
                  key={m.label}
                  className="rounded-[8px] border border-border bg-card p-6 text-center shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                >
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-2xl md:text-3xl font-bold text-primary leading-none">
                      {m.value}
                    </span>
                    <span className="text-sm font-semibold text-accent">{m.unit}</span>
                  </div>
                  <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                    {m.label}
                  </p>
                </div>
              ))}
              <div className="sm:col-span-3 rounded-[8px] border border-border bg-primary p-6 text-white">
                <div className="flex items-center gap-3">
                  <BarChart3 size={22} strokeWidth={1.6} className="text-accent" />
                  <p className="text-sm font-semibold">
                    データドリブンな接客で、継続的な改善サイクルを実現
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* @section: cases */}
      <section id="cases" className="bg-secondary py-20 md:py-28">
        <div className="container mx-auto px-4 md:px-6">
          <SectionHeading
            eyebrow="Use Cases"
            title="業種別 活用事例"
            description="店頭・対面接客が起点になる業種ほど、LINE連携による顧客の見える化と再来店づくりが効果を発揮します。"
          />

          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {CASES.map((c, i) => {
              const Icon = CASE_ICONS[i];
              return (
                <div
                  key={c.no}
                  className="flex flex-col rounded-[8px] border border-border bg-card p-7 md:p-8 shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3.5">
                      <span className="grid place-items-center h-12 w-12 rounded-[3px] bg-primary text-white">
                        <Icon size={22} strokeWidth={1.6} />
                      </span>
                      <div>
                        <h3 className="text-lg font-bold text-primary leading-tight">
                          {c.industry}
                        </h3>
                        <span className="font-mono text-xs font-medium tracking-wide text-muted-foreground">
                          {c.tag}
                        </span>
                      </div>
                    </div>
                    <span className="font-mono text-sm font-medium text-muted-foreground">
                      {c.no}
                    </span>
                  </div>

                  <div className="mt-6 space-y-4">
                    <div>
                      <p className="text-xs font-semibold tracking-wide text-accent">課題</p>
                      <p className="mt-1.5 text-sm leading-relaxed text-secondary-foreground">
                        {c.challenge}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold tracking-wide text-accent">linectの活用</p>
                      <p className="mt-1.5 text-sm leading-relaxed text-secondary-foreground">
                        {c.solution}
                      </p>
                    </div>
                  </div>

                  <ul className="mt-5 space-y-2.5 border-t border-border pt-5">
                    {c.results.map((r) => (
                      <li key={r} className="flex items-start gap-2.5">
                        <span className="mt-0.5 grid place-items-center h-5 w-5 shrink-0 rounded-[2px] bg-accent/12 text-accent">
                          <Check size={13} strokeWidth={2.4} />
                        </span>
                        <span className="text-sm text-secondary-foreground">{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* @section: flow */}
      <section id="flow" className="bg-secondary py-20 md:py-28">
        <div className="container mx-auto px-4 md:px-6">
          <SectionHeading
            align="center"
            eyebrow="Flow"
            title="導入までの流れ"
            description="現状のヒアリングから設計・導入・運用改善まで、伴走して支援します。"
          />
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {FLOW.map((s, i) => (
              <div
                key={s.no}
                className="relative rounded-[8px] border border-border bg-card p-6 shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
              >
                <span className="font-mono text-xs font-semibold tracking-wide text-accent">
                  {s.no}
                </span>
                <div className="mt-3 text-3xl font-bold text-primary/15 font-mono leading-none">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="mt-3 text-base font-bold text-primary leading-snug">
                  {s.title}
                </h3>
                <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* @section: pricing */}
      <section id="pricing" className="py-20 md:py-28">
        <div className="container mx-auto px-4 md:px-6">
          <SectionHeading
            align="center"
            eyebrow="Pricing"
            title="料金"
            description="わかりやすい一律料金。導入規模に応じた個別見積りにも対応します。"
          />

          <div className="mx-auto mt-14 max-w-3xl">
            <div className="grid items-stretch gap-0 overflow-hidden rounded-[8px] border border-border bg-card shadow-[0_1px_3px_rgba(0,0,0,0.08)] md:grid-cols-2">
              {/* 価格パネル */}
              <div className="flex flex-col justify-center bg-primary p-8 md:p-10 text-white">
                <span className="inline-flex w-fit items-center gap-2 rounded-[4px] bg-white/10 px-3 py-1.5 text-xs font-semibold tracking-wide">
                  <span className="h-2 w-2 rounded-[1px] bg-accent" />
                  一律プラン
                </span>
                <div className="mt-6 flex items-baseline gap-1.5">
                  <span className="text-5xl md:text-6xl font-bold leading-none tracking-tight">
                    {PRICING.price}
                  </span>
                  <span className="text-base font-semibold text-white/80">
                    {PRICING.unit}
                  </span>
                </div>
                <p className="mt-6 text-sm leading-relaxed text-white/75">
                  {PRICING.note}
                </p>
              </div>

              {/* 内容パネル */}
              <div className="flex flex-col justify-center p-8 md:p-10">
                <p className="text-sm font-bold text-primary">プランに含まれるもの</p>
                <ul className="mt-5 space-y-3.5">
                  {PRICING.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-3">
                      <span className="mt-0.5 grid place-items-center h-5 w-5 shrink-0 rounded-[2px] bg-accent/12 text-accent">
                        <Check size={13} strokeWidth={2.4} />
                      </span>
                      <span className="text-sm leading-relaxed text-secondary-foreground">
                        {pt}
                      </span>
                    </li>
                  ))}
                </ul>
                <a
                  href={CONTACT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-7 inline-flex w-fit items-center justify-center gap-1.5 rounded-[4px] bg-accent px-6 py-3 text-sm font-semibold text-accent-foreground hover:opacity-90 transition-opacity"
                >
                  料金について問い合わせる
                  <ArrowUpRight size={16} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* @section: faq */}
      <section id="faq" className="py-20 md:py-28">
        <div className="container mx-auto px-4 md:px-6">
          <SectionHeading
            align="center"
            eyebrow="FAQ"
            title="よくあるご質問"
          />
          <div className="mx-auto mt-12 max-w-3xl divide-y divide-border border-y border-border">
            {FAQS.map((f) => (
              <div key={f.q} className="py-6">
                <h3 className="flex gap-3 text-base font-bold text-primary">
                  <span className="text-accent font-mono">Q.</span>
                  {f.q}
                </h3>
                <p className="mt-2.5 pl-7 text-sm leading-relaxed text-muted-foreground">
                  {f.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* @section: contact-cta */}
      <section id="contact" className="bg-primary py-20 md:py-24">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white leading-tight">
            LINEを起点に、顧客との関係を
            <br className="hidden md:block" />
            次のステージへ。
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base md:text-lg leading-relaxed text-white/75">
            導入のご相談・お見積りはお気軽にお問い合わせください。貴社の課題に合わせた活用方法をご提案します。
          </p>
          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={CONTACT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-1.5 rounded-[4px] bg-accent px-8 py-4 text-base font-semibold text-accent-foreground hover:opacity-90 transition-opacity"
            >
              お問い合わせ
              <ArrowUpRight size={18} />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
